<template>
  <div class="station-main-panel">
    <div class="station-leftbar">
      <div class="station-leftbar-graph">
        <StationPerformanceGraph ref="performanceChart" :productionStation="productionStation" v-if="productionStation.showStationGraph" />
      </div>

      <StationButtonGroup>
        <StationButtonGroupButton class="col-4" :active="scheduleTabIndex == 0" @click="scheduleTabIndex = 0">
          ASSEMBLIES
        </StationButtonGroupButton>
        <StationButtonGroupButton class="col-4" :active="scheduleTabIndex == 1" @click="scheduleTabIndex = 1">
          ITEMS
        </StationButtonGroupButton>
        <StationButtonGroupButton class="col-4" :active="scheduleTabIndex == 2" @click="scheduleTabIndex = 2">
          ORDERS
        </StationButtonGroupButton>
      </StationButtonGroup>

      <div class="station-leftbar-list station-leftbar-list-with-button-group" ref="scheduleLeftBar">
        <StationPanelScheduleList 
          v-show="scheduleTabIndex == 0"
          :stationId="$route.params.id"
          :filterPanelType="ProductionPanelTypes.assembly"
          @panelClick="panelClicked"
          @urgentCountUpdated="$emit('urgentCountUpdated', $event)"
        />

        <StationPanelScheduleList 
          v-show="scheduleTabIndex == 1"
          :stationId="$route.params.id"
          :filterPanelType="ProductionPanelTypes.glass"
          @panelClick="panelClicked"
          @urgentCountUpdated="$emit('urgentCountUpdated', $event)"
        />

        <StationOrderScheduleList 
          v-show="scheduleTabIndex == 2"
          :stationId="$route.params.id"
          @orderClick="orderClicked"
        />
      </div>
    </div>
    <div class="station-mainarea">
      <div class="station-tool-window">      
        <div class="station-view" @click="focusBarcodeEntry()">
          <div class="station-main-general-view">
            <div class="row">
              <div class="col-6">
                <div class="station-scanner-entry">
                  <h2 class="text-muted">Enter assembly barcode to scan an item for {{productionStage.name}}</h2>
                  <div class="row">
                    <div class="col-10">
                      <FormInputText ref="panelBarcodeScan" v-model="barcodeEntry" placeholder="Barcode" @enter="onScannedBarcode()" prependSlot>
                        <template v-slot:prepend>
                          <i class="far fa-scanner"></i>
                        </template>
                      </FormInputText>
                    </div>
                    <div class="col-2">
                      <StationControlPanelButton class="btn-default" sizeClass="xl" iconStyle="fal" icon="fa-scanner-keyboard" @click="scanOptionsClicked">Scan Options</StationControlPanelButton>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <StationAlertMessage ref="alertMessage" />
              </div>
            </div>

            <h2>{{productionStation.name}} Assembly Queue</h2>
            <div class="station-list-container station-assembly-droppable-container">
              <div class="station-assembly-dropzone" v-show="showAssemblyDrop">Drop assemblies here to start</div>

              <div ref="queueList" class="station-list station-thumb-layout">
              
                <QueueAssemblyThumbItem
                  v-for="assembly of assemblyPanelList"
                  :key="assembly.id"
                  :panel="assembly"
                  :selected="currentSelectedAssemblyPanelId == assembly.id"
                  :highlightColour="productionStage.highlightColour"
                  :productionStationId="productionStation.id"
                  :productionStageId="productionStage.id"
                  :queueDisplayType="productionStation.queueDisplayType"
                  :ref="`panel-queue-${assembly.id}`"
                  @scanCompleteItem="scanCompleteAssemblySubItem(assembly, $event)"
                  @printLabelClick="printPanelLabel($event)"
                  @reprintLabelClick="reprintPanelLabel($event)"
                  @previewClicked="queuePanelPreviewClicked($event, assembly)"
                  @panelClicked="queuePanelClicked($event, assembly)"
                  @removePanel="removeAssemblyFromStation(assembly.id)">
                </QueueAssemblyThumbItem>
              
                <h2 class="text-muted text-center" v-if="assemblyPanelList.length == 0 && stationCanScanIn">No items at this station</h2>
                <h2 class="text-muted text-center" v-if="assemblyPanelList.length == 0 && !stationCanScanIn">No items at stations visible to this station</h2>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.station-main-general-view {
  flex: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.station-assembly-dropzone {
  box-sizing: border-box;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  font-size: 26px;
  background: rgba(32, 32, 32, .6);
  color: #FFFFFF;
  border: 3px dashed #808080;
  border-radius: 10px;
  z-index: 10;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>

<script>
import _ from 'lodash'
import dragula from 'dragula'
import 'dragula/dist/dragula.css'

import formMixin from '@/mixins/form'
import Utils from '@/utils/utils'
import EventBus from '@/utils/eventBus'

import ProductionStationMixin from '@/apps/glass-production-station/src/mixins/productionStation'

import StationButtonGroup from '@/apps/glass-production-station/src/components/main/stationButtonGroup'
import StationButtonGroupButton from '@/apps/glass-production-station/src/components/main/stationButtonGroupButton'
import StationPanelScheduleList from '@/apps/glass-production-station/src/components/schedule/stationPanelScheduleList'
import StationOrderScheduleList from '@/apps/glass-production-station/src/components/schedule/stationOrderScheduleList'
import StationPerformanceGraph from '@/apps/glass-production-station/src/components/stationPerformanceGraph'

import QueueAssemblyThumbItem from '@/apps/glass-production-station/src/components/station/queueAssemblyThumbItem'
import StationAlertMessage from '@/apps/glass-production-station/src/components/station/stationAlertMessage'
import FastForwardCompleteDialog from '@/apps/glass-production-station/src/views/fastForwardCompleteDialog'
import StationControlPanelButton from '@/apps/glass-production-station/src/components/buttons/stationControlPanelButton'
import StationScanWarningDialog from '@/apps/glass-production-station/src/views/stationScanWarningDialog'

import { ProductionStationQueueDisplayTypes, ProductionPanelTypes, ProductionPanelTypeLookup } from '@/store/types'


export default {
  name: 'AssemblyStationView',
  mixins: [formMixin, ProductionStationMixin],
  components: {
    StationButtonGroup,
    StationButtonGroupButton,
    StationPanelScheduleList,
    StationOrderScheduleList,
    StationPerformanceGraph,
    StationAlertMessage,
    QueueAssemblyThumbItem,
    StationControlPanelButton
  },
  props: {
    productionStage: Object,
    productionStation: Object
  },
  data() {
    return {
      scheduleTabIndex: 0,
      barcodeEntry: '',
      currentSelectedAssemblyPanelId: null,
      showAssemblyDrop: false,
      panelScanSuccess: false,
      panelScanError: false,
      panelDetails: {
        barcode: '',
        nextStage: '',
        nextStageColour: '#400000',
        tasksComplete: false,
        despatchType: '',
        panelTaskStarted: false,
        panelTaskCompleted: false,
      },
      panelScanErrorMessage: ''
    }
  },
  computed: {
    stationHasSecondaryStation() {
      return this.productionStation.secondaryStationId != null
    },
    secondaryStationName() {
      if(this.productionStation.secondaryStation)
        return this.productionStation.secondaryStation.name
      return ''
    },
    stationCanScanIn() {
      if(this.productionStation)
        return this.productionStation.isScanInAvailable
      return false
    },
    stationCanScanOut() {
      if(this.productionStation)
        return this.productionStation.isScanOutAvailable
      return false
    },
    isStationQueueEditorView() {
      if(this.productionStation)
        return this.productionStation.queueDisplayType == ProductionStationQueueDisplayTypes.editorView
      return false
    },
    assemblyPanelList() {
      if(!this.productionStation)
        return []
      return this.productionStation.productionPanels.filter(panel => panel.type == ProductionPanelTypes.assembly)
    },
    isAssemblySelected() {
      return this.currentSelectedAssemblyPanelId != null
    }
  },
  watch: {
    assemblyPanelList: {
      immediate: true,
      handler: function(newVal, oldVal) {
        // Only scroll down if new panels added
        if(newVal && oldVal) {
          if(newVal.length > oldVal.length) {
            this.$nextTick(() => {
              this.queueListUpdated()
            })
          }
        }

        // If the selected assembly is no longer in the list, clear it
        if(this.currentSelectedAssemblyPanelId) {
          if(!this.assemblyPanelList.some(panel => panel.id == this.currentSelectedAssemblyPanelId))
            this.currentSelectedAssemblyPanelId = null
        }

        // If no assembly selected, select the first one
        if(!this.currentSelectedAssemblyPanelId && this.assemblyPanelList.length > 0) {
          this.currentSelectedAssemblyPanelId = this.assemblyPanelList[0].id
        }
      }
    }
  },
  created() {
    this.ProductionPanelTypes = ProductionPanelTypes
  },
  mounted() {
    this.$refs.panelBarcodeScan.select()
    this.$refs.panelBarcodeScan.focus()

    EventBus.on(EventBus.EventTypes.panelLabelPrinted, this.focusBarcodeEntry)

    this.setupDraggablePanels()
  },
  beforeDestroy() {
    EventBus.off(EventBus.EventTypes.panelLabelPrinted, this.focusBarcodeEntry)
  },
  methods: {
    setupDraggablePanels() {
      // Setup Drag & Drop support for assembly panels in the optimiser
      let stationView = this
      this.$options.drake = dragula([this.$refs.scheduleLeftBar], {
        copy: true,
        removeOnSpill: true,
        revertOnSpill: true,
        slideFactorX: 5,
        slideFactorY: 5,
        isContainer: function(el) {
          if(el.classList.contains('station-assembly-draggable-container') || el.classList.contains('station-assembly-droppable-container'))
            return true

          return false
        },
        moves: function (el, source, handle, sibling) {
          return el.classList.contains('station-assembly-draggable');
        },
        accepts: function (el, target, source, sibling) {
          if(!el)
            return false
          
          if(el.dataset.type == 'assembly') {
            if(target.classList.contains('station-assembly-droppable-container'))
              return true
          }

          return false
        }
      })
      this.$options.drake.on('out', (el, container, source) => {
        let isAssembly = el.classList.contains('station-assembly-draggable')

        if(isAssembly && container.classList.contains('station-assembly-droppable-container'))
          stationView.showAssemblyDrop = false
      })
      this.$options.drake.on('over', (el, container, source) => {
        let isAssembly = el.classList.contains('station-assembly-draggable')

        if(isAssembly && container.classList.contains('station-assembly-droppable-container'))
          stationView.showAssemblyDrop = true
      })
      this.$options.drake.on('drop', (el, container, source) => {
        this.$options.drake.cancel(true)

        // Handle dropping the assembly
        if(el.dataset.type == 'assembly') {
          if(container.classList.contains('station-assembly-droppable-container')) {
            stationView.barcodeEntry = el.dataset.barcode
            stationView.onScannedBarcode()
          }
        }
      })
    },
    onScannedBarcode(options = {}) {
      if(this.barcodeEntry.trim().length == '') {
        this.barcodeEntry = ''
        return
      }

      var scanData = {
        barcode: this.barcodeEntry
      }
      if(options)
        scanData = _.merge(scanData, options)

      // Run the scan in/out function on the barcode scanned
      // If we have a selected assembly, run the scan through that
      var scanFuncPromise = null
      if(this.isAssemblySelected)
        scanFuncPromise = this.$apiService.glassProductionStationService.runAssemblyBarcodeScan(this.productionStation.id, this.currentSelectedAssemblyPanelId, scanData)
      else
        scanFuncPromise = this.$apiService.glassProductionStationService.runPanelBarcodeScan(this.productionStation.id, scanData)

      scanFuncPromise
      .then(response => {
        this.$refs.alertMessage.hide()

        let barcode = response.data.panelBarcode
        let scanResultData = response.data
        var nextStage = ''

        if(scanResultData.panelTaskCompleted) {
          if(scanResultData.nextStage && scanResultData.nextStage.productionStage) {
            nextStage = scanResultData.nextStage.productionStage.name
          }
        }

        let panelName = 'Panel'
        if(scanResultData.productionPanel) {
          panelName = this.formatLookupName(scanResultData.productionPanel.type, ProductionPanelTypeLookup)
        }

        if(scanResultData.tasksComplete) {
          var despatchName = scanResultData.despatchTaskType ? scanResultData.despatchTaskType.name : ''
          this.$refs.alertMessage.displayAlert('success', `${panelName} Completed`, `${panelName} ${barcode} has finished processing and is due at <strong>despatch</strong> for <strong>${despatchName}</strong>`)
        } else {
          if(scanResultData.panelTaskStarted)
            this.$refs.alertMessage.displayAlert('success', `${panelName} Scanned In`, `${panelName} ${barcode} added`)
          else if(scanResultData.panelTaskCompleted)
            this.$refs.alertMessage.displayAlert('success', `${panelName} Scanned Out`, `${panelName} ${barcode} is next due at <strong>${nextStage}</strong>`)
        }

        if(scanResultData.assemblyUpdated) {
          if(scanResultData.productionPanel) {
            barcode = scanResultData.productionPanel.barcode
            this.$refs.alertMessage.displayAlert('success', `${panelName} scanned into assembly`, `${panelName} ${barcode} added`)
          }
          if(scanResultData.stock) {
            barcode = scanResultData.stock.barcode
            this.$refs.alertMessage.displayAlert('success', `Stock scanned into assembly`, `${scanResultData.stock.code} added`)
          }

          // Reload the assembly when updated
          if(this.currentSelectedAssemblyPanelId) {
            if(this.$refs[`panel-queue-${this.currentSelectedAssemblyPanelId}`]) {
              let assemblyQueueItem = this.$refs[`panel-queue-${this.currentSelectedAssemblyPanelId}`][0]
              if(assemblyQueueItem)
                assemblyQueueItem.reloadData()
            }
          }
        }

        // Back to scanning
        this.barcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()
      })
      .catch(err => {
        let errorInfo = this.processFormResponse(err)
        
        this.$refs.alertMessage.hide()

        // If the server gives fast forward options, display the fast forward dialog
        if(errorInfo.errorData && errorInfo.errorData.fastForwardOptions && errorInfo.errorData.fastForwardOptions.available) {

          // Show fast forward complete options
          this.displayModal(FastForwardCompleteDialog, { 
            productionStageId: this.productionStage.id,
            productionStationId: this.productionStation.id,
            fastForwardOptions: errorInfo.errorData.fastForwardOptions,
          })
          .then((result) => {

            if(result.allTasksCompleted) {
              // Attempt to automatically scan in the panel again now we have completed all pending tasks
              this.$nextTick(() => {
                this.onScannedBarcode()
              })
              return
            } else {
              this.$refs.alertMessage.displayAlert('success', 'Tasks Completed', 'Pending Tasks completed, ready for scanning')
            }

            // Back to scanning
            this.barcodeEntry = ''
            this.$refs.panelBarcodeScan.focus()
          })          
          return
        }

        // If the server gives warnings + override, display the override warning dialog
        if(errorInfo.errorData && errorInfo.errorData.overrideWarningOptions && errorInfo.errorData.overrideWarningOptions.available) {

          // Show warning override options
          this.displayModal(StationScanWarningDialog, { 
            productionStageId: this.productionStage.id,
            productionStationId: this.productionStation.id,
            overrideWarningOptions: errorInfo.errorData.overrideWarningOptions,
          })
          .then((result) => {

            if(result) {
              // Attempt to scan again with override specified
              this.$nextTick(() => {
                this.onScannedBarcode({ overrideWarnings: true, overrideName: result.name })
              })
            }
          })          
          return
        }

        this.$refs.alertMessage.displayAlert('error', 'Scanning Error!', errorInfo.errorMessage)

        // Back to scanning
        this.barcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()
      })
    },

    removeAssemblyFromStation(productionPanelId) {
      // Remove assembly
      this.$apiService.glassProductionStationService.removeAssemblyFromStation(this.productionStation.id, productionPanelId)
      .then(response => {
        this.$refs.alertMessage.hide()
        this.$refs.panelBarcodeScan.select()
      })
      .catch(err => {
        let errorInfo = this.processFormResponse(err)
        this.$refs.alertMessage.displayAlert('error', 'Error Removing Assembly!', errorInfo.errorMessage)

        // Back to scanning
        this.barcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()
      })
    },

    removePanelFromStation(productionPanelId) {
      // Remove panel
      this.$apiService.glassProductionStationService.removePanelFromStation(this.productionStation.id, { productionPanelId: productionPanelId })
      .then(response => {
        this.$refs.alertMessage.hide()

        this.$refs.panelBarcodeScan.select()
      })
      .catch(err => {
        let errorInfo = this.processFormResponse(err)
        this.$refs.alertMessage.displayAlert('error', 'Error Removing Panel!', errorInfo.errorMessage)

        // Back to scanning
        this.barcodeEntry = ''
        this.$refs.panelBarcodeScan.focus()
      })
    },
    queuePanelClicked(event, panel) {
      let panelComponent = this.$refs[`panel-queue-${panel.id}`][0]

      this.$emit('panelClick', { panel: panel, target: panelComponent.$el, forceElement: true })
    },
    queuePanelPreviewClicked(event, panel) {
      let panelComponent = this.$refs[`panel-queue-${panel.id}`][0]

      this.$emit('panelPreviewClick', { panel: panel, target: panelComponent.$el, forceElement: true })
    },
    focusBarcodeEntry() {
      // Ignore if a modal dialog if open
      if(this.isDocumentModalVisible())
        return

      this.$refs.panelBarcodeScan.select()
    },
    queueListUpdated() {
      // Scroll to the last panel scanned
      if(this.isStationQueueEditorView && this.$refs.queueList) {
        this.$refs.queueList.scrollTop = this.$refs.queueList.scrollHeight
      }
    },
    scanOptionsClicked() {
      this.displayStationScanOptions(this.productionStation.id)
    },
    panelClicked(info) {
      this.$emit('panelClick', { panel: info.panel, target: this.$refs.scheduleLeftBar, forceElement: true })
    },
    orderClicked(info) {
      this.$emit('orderClick', { order: info.order, target: this.$refs.scheduleLeftBar, forceElement: true })
    },
    scanCompleteAssemblySubItem(assembly, barcode) {
      this.currentSelectedAssemblyPanelId = assembly.id
      this.barcodeEntry = barcode
      this.onScannedBarcode()
    }
  }
  
}
</script>